import { useMemo } from "react";

import {
  Badge,
  FontIcon,
  Table,
  TableColumnDefinition,
  TableQuery,
  TableRowDefinition,
} from "@ezmid/components-library";
import {
  faFingerprint,
  faInfoCircle,
  faList,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useGetBackOfficeListSubscriptionPackagesQuery } from "../../../api/subscription-packages.generated";
import { useFormatters } from "../../../hooks";
import { makeRoute } from "../../../hooks/route";
import { useTableQuery } from "../../../hooks/useTableQuery";
import { AllRoutes } from "../../route/Route";

const localStorageKey = "subscription.page";

type TableData = {
  id: string;
  name: string;
  description: string;
  plan: string;
  duration: number;
  credits: number;
  status: string;
  createdAt: string;
};

export function SubscriptionsListPageContent<T extends object>() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatDate } = useFormatters();
  const { query, updateQuery } = useTableQuery(
    localStorageKey,
    {
      pageSize: 10,
      pageIndex: 1,
      keyword: "",
      sortBy: { key: "createdAt", order: "desc" },
      columnCategoryFilter: [],
    },
    AllRoutes.SUBSCRIPTIONS_LIST
  );

  const { currentData, isFetching } =
    useGetBackOfficeListSubscriptionPackagesQuery(
      {
        page: query?.pageIndex,
        size: query?.pageSize,
        keyword: query?.keyword,
        sort: [`${query.sortBy?.key}:${query.sortBy?.order}`],
      },
      {
        refetchOnMountOrArgChange: true,
      }
    );

  const memoizedData: Array<TableData> = useMemo(
    () =>
      currentData?.items?.map((pkg) => {
        return {
          id: pkg?.id || "",
          name: pkg?.name || "",
          description: pkg?.description || "",
          plan: pkg?.plan || "",
          duration: pkg?.duration || 0,
          credits: pkg?.credits || 0,
          status: pkg?.status || "",
          createdAt: pkg?.createdAt || "",
        };
      }) || [],
    [currentData]
  );

  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.id"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            leftIcon={
              <FontIcon
                marginTop={4}
                customIcon={<FontAwesomeIcon size="lg" icon={faFingerprint} />}
              />
            }
          >
            {row?.original.id.toUpperCase().substr(0, 8)}
          </Badge>
        ),
        id: "id",
        accessor: "id",
        width: "200px",
      },
      {
        Header: t("table.header.name"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge variant="pill">{row?.original.name ?? ""}</Badge>
        ),
        id: "name",
        accessor: "name",
        width: "200px",
      },
      {
        Header: t("table.header.plan"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge variant="pill">
            {row?.original.plan?.split("-")[1] || ""}
          </Badge>
        ),
        id: "plan",
        accessor: "plan",
        width: "150px",
      },
      {
        Header: t("table.header.duration"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <span>{row?.original.duration}</span>
        ),
        id: "duration",
        accessor: "duration",
        width: "120px",
      },
      {
        Header: t("table.header.credits"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <span>{row?.original.credits}</span>
        ),
        id: "credits",
        accessor: "credits",
        width: "120px",
      },
      {
        Header: t("table.header.status"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          const status =
            row?.original.status === "sps-active" ? "success" : "danger";

          return (
            <Badge status={status} variant="pill">
              {row?.original.status.split("-")[1]}
            </Badge>
          );
        },
        id: "status",
        accessor: "status",
        width: "150px",
      },
      {
        Header: t("table.header.description"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <span className="truncate max-w-[200px] block">
            {row?.original.description ?? ""}
          </span>
        ),
        id: "description",
        accessor: "description",
        width: "200px",
      },
      {
        Header: t("table.header.createdAt"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <span>{formatDate(row?.original.createdAt)}</span>
        ),
        id: "createdAt",
        accessor: "createdAt",
        width: "200px",
      },
    ],
    []
  );

  const onFetchData = ({
    pageIndex,
    pageSize,
    keyword: queryKeyword,
    sortBy,
    columnCategoryFilter,
  }: TableQuery) => {
    updateQuery({
      pageIndex,
      pageSize,
      keyword: queryKeyword || "",
      sortBy,
      columnCategoryFilter,
    });
  };

  return (
    <div className="p-2 w-full min-w-full max-h-fit">
      <div>
        <Table
          headerIcon={
            <FontIcon
              marginTop={4}
              customIcon={<FontAwesomeIcon icon={faList} />}
            />
          }
          headerText={t("card.header.list")}
          tableData={memoizedData}
          tableColumns={columns}
          showPagination
          showToolbar
          onFetchData={onFetchData}
          onRowSelect={(row) =>
            navigate(
              makeRoute(AllRoutes.SUBSCRIPTION_PACKAGE_DETAIL, {
                id: row.original?.id,
              })
            )
          }
          totalCountOfRecords={currentData?.count || 0}
          query={{
            pageIndex: query.pageIndex,
            pageSize: query.pageSize,
            keyword: query.keyword,
            sortBy: query.sortBy,
          }}
          isLoading={isFetching}
          shouldAddEmptyRowsOnLastPage={currentData?.last}
          columnTypeFilter={[
            {
              label: t("app.status.subscriptionPackage.active"),
              category: "sps-active",
              icon: (
                <FontAwesomeIcon
                  style={{ marginTop: "1px" }}
                  icon={faInfoCircle}
                />
              ),
            },
            {
              label: t("app.status.subscriptionPackage.inactive"),
              category: "sps-inactive",
              icon: (
                <FontAwesomeIcon
                  style={{ marginTop: "1px" }}
                  icon={faInfoCircle}
                />
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
